import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Plus, Loader2 } from 'lucide-react';

/**
 * Pulsante "Aggiungi al carrello" riusabile per il sito pubblico.
 * - Login richiesto: se l'utente non è un cliente loggato, salva l'intento e reindirizza al login
 *   (con `next`), poi l'aggiunta viene completata in automatico dal CartContext.
 * - Se loggato: aggiunge la voce e apre il mini-carrello.
 *
 * @param {string} catalogId - es. "experience:<id>", "excursion:<id>", "service:<mongoid>"
 */
export function AddToCartButton({
  catalogId, title = 'questa voce', label = 'Aggiungi al carrello',
  size = 'default', variant = 'default', className = '', iconOnly = false, stopPropagation = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem, openMiniCart, isCustomer } = useCart() || {};
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    if (stopPropagation) { e.preventDefault(); e.stopPropagation(); }
    if (!catalogId) return;
    if (!isCustomer) {
      try { sessionStorage.setItem('sila_pending_add', catalogId); } catch (err) { /* noop */ }
      const next = encodeURIComponent(location.pathname + location.search);
      toast.info('Accedi per aggiungere al carrello');
      navigate(`/cliente/accedi?next=${next}`);
      return;
    }
    setLoading(true);
    try {
      await addItem(catalogId, 1);
      openMiniCart();
      toast.success(`"${title}" aggiunto al carrello`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || 'Voce non disponibile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button type="button" size={size} variant={variant} className={className} onClick={handle}
      disabled={loading} data-testid={`add-to-cart-${catalogId}`} aria-label={label}>
      {loading ? <Loader2 className={iconOnly ? 'h-4 w-4 animate-spin' : 'mr-2 h-4 w-4 animate-spin'} />
        : (iconOnly ? <ShoppingCart className="h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />)}
      {!iconOnly && label}
    </Button>
  );
}

export default AddToCartButton;
